import { Link } from 'react-router-dom';
import SEOHead from '../../components/base/SEOHead';
import Header from '../../components/feature/Header';

export default function Services() {
  const servicesSchema = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "道路工事現場の交通誘導",
      "provider": {
        "@type": "LocalBusiness",
        "name": "おかやま警備株式会社"
      },
      "areaServed": {
        "@type": "City",
        "name": "倉敷市"
      },
      "description": "倉敷市内の道路工事や舗装工事における車両・歩行者の安全な誘導を行います。工事区間の適切な交通規制と円滑な交通流の確保により、事故を未然に防ぎます。"
    },
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "建設現場の警備",
      "provider": {
        "@type": "LocalBusiness",
        "name": "おかやま警備株式会社"
      },
      "areaServed": {
        "@type": "City",
        "name": "倉敷市"
      },
      "description": "ビル建設や住宅建築現場での車両誘導と安全管理を実施します。工事車両の出入り管理、資材搬入時の交通整理など、建設現場特有のニーズに対応。"
    },
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "イベント会場の交通整理",
      "provider": {
        "@type": "LocalBusiness",
        "name": "おかやま警備株式会社"
      },
      "areaServed": {
        "@type": "City",
        "name": "倉敷市"
      },
      "description": "祭りやスポーツイベント、コンサートなどの大規模イベントにおける駐車場誘導と交通整理を行います。"
    },
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "商業施設の駐車場警備",
      "provider": {
        "@type": "LocalBusiness",
        "name": "おかやま警備株式会社"
      },
      "areaServed": {
        "@type": "City",
        "name": "倉敷市"
      },
      "description": "ショッピングセンターや商業施設の駐車場における交通誘導と安全管理を実施。"
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "ホーム",
          "item": "SITE_URL_PLACEHOLDER/"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "事業内容",
          "item": "SITE_URL_PLACEHOLDER/services"
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="事業内容｜倉敷市交通誘導警備｜おかやま警備株式会社"
        description="おかやま警備株式会社の事業内容。倉敷市を中心に道路工事現場の交通誘導、建設現場の警備、イベント会場の交通整理、商業施設の駐車場警備など、様々な現場で安全を確保する交通誘導警備サービスを提供しています。"
        keywords="事業内容,交通誘導警備,道路工事,建設現場,イベント警備,駐車場警備,倉敷市,警備サービス"
        canonical="/services"
        schema={servicesSchema}
      />
      <Header />

      {/* Hero Section */}
      <section className="relative h-96 flex items-center justify-center bg-cover bg-center" style={{backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('https://public.readdy.ai/ai/img_res/0b23c3372a4f8e60c9cbc3e75beb9aac.jpg')`}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center w-full">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">事業内容</h1>
          <p className="text-xl md:text-2xl text-white">倉敷市を中心に、様々な現場で安全を確保する交通誘導警備サービス</p>
        </div>
      </section>

      {/* Services Detail */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-20">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-12">
              <div>
                <img src="https://readdy.ai/api/search-image?query=Japanese%20security%20guard%20in%20orange%20safety%20vest%20directing%20traffic%20at%20road%20construction%20site%20with%20professional%20hand%20signals%2C%20construction%20equipment%20and%20workers%20in%20background%2C%20daytime%20clear%20weather%2C%20professional%20photography%20style%2C%20realistic%20high%20quality&width=800&height=600&seq=service101&orientation=landscape" alt="道路工事現場での交通誘導" className="w-full h-96 object-cover object-top rounded-xl shadow-lg" />
              </div>
              <div>
                <div className="inline-block bg-blue-100 text-blue-900 px-4 py-2 rounded-full text-sm font-semibold mb-4">主要サービス</div>
                <h2 className="text-4xl font-bold text-gray-900 mb-6">道路工事現場の交通誘導</h2>
                <p className="text-gray-700 leading-relaxed mb-6">倉敷市内の道路工事や舗装工事における車両・歩行者の安全な誘導を行います。工事区間の適切な交通規制と円滑な交通流の確保により、事故を未然に防ぎます。主要道路から生活道路まで、あらゆる道路工事に対応可能です。</p>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-blue-900 mr-3 mt-1"></i>
                    <span className="text-gray-700"><strong>片側交互通行の管理：</strong>工事による車線規制時の安全な交互通行を実現</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-blue-900 mr-3 mt-1"></i>
                    <span className="text-gray-700"><strong>歩行者の安全確保：</strong>工事区間を通行する歩行者の安全な誘導</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-blue-900 mr-3 mt-1"></i>
                    <span className="text-gray-700"><strong>夜間工事対応：</strong>24時間体制での交通誘導が可能</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-blue-900 mr-3 mt-1"></i>
                    <span className="text-gray-700"><strong>緊急車両対応：</strong>救急車両等の優先通行を適切に処理</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="mb-20">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <div className="inline-block bg-blue-100 text-blue-900 px-4 py-2 rounded-full text-sm font-semibold mb-4">専門サービス</div>
                <h2 className="text-4xl font-bold text-gray-900 mb-6">建設現場の警備</h2>
                <p className="text-gray-700 leading-relaxed mb-6">ビル建設や住宅建築現場での車両誘導と安全管理を実施します。工事車両の出入り管理、資材搬入時の交通整理など、建設現場特有のニーズに対応。近隣住民への配慮も含めた総合的な警備サービスを提供します。</p>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-blue-900 mr-3 mt-1"></i>
                    <span className="text-gray-700"><strong>工事車両の出入り管理：</strong>大型車両の安全な出入りをサポート</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-blue-900 mr-3 mt-1"></i>
                    <span className="text-gray-700"><strong>資材搬入時の交通整理：</strong>クレーン作業時などの周辺交通管理</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-blue-900 mr-3 mt-1"></i>
                    <span className="text-gray-700"><strong>近隣住民への配慮：</strong>騒音や振動への配慮と丁寧な対応</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-blue-900 mr-3 mt-1"></i>
                    <span className="text-gray-700"><strong>長期現場対応：</strong>数ヶ月〜数年の長期プロジェクトにも対応</span>
                  </li>
                </ul>
              </div>
              <div className="order-1 lg:order-2">
                <img src="https://readdy.ai/api/search-image?query=Japanese%20security%20personnel%20in%20professional%20uniform%20managing%20construction%20site%20entrance%20with%20large%20building%20under%20construction%20in%20background%2C%20organized%20work%20environment%2C%20safety%20cones%20and%20barriers%2C%20daytime%20professional%20photography%2C%20realistic%20high%20quality&width=800&height=600&seq=service102&orientation=landscape" alt="建設現場の警備" className="w-full h-96 object-cover object-top rounded-xl shadow-lg" />
              </div>
            </div>
          </div>

          <div className="mb-20">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <img src="https://readdy.ai/api/search-image?query=Japanese%20security%20staff%20directing%20vehicles%20at%20large%20outdoor%20festival%20event%20with%20crowds%20of%20people%2C%20organized%20parking%20area%20with%20clear%20signage%2C%20festive%20atmosphere%2C%20daytime%20sunny%20weather%2C%20professional%20event%20photography%20style%2C%20realistic%20high%20quality&width=800&height=600&seq=service103&orientation=landscape" alt="イベント会場の交通整理" className="w-full h-96 object-cover object-top rounded-xl shadow-lg" />
              </div>
              <div>
                <div className="inline-block bg-blue-100 text-blue-900 px-4 py-2 rounded-full text-sm font-semibold mb-4">イベント対応</div>
                <h2 className="text-4xl font-bold text-gray-900 mb-6">イベント会場の交通整理</h2>
                <p className="text-gray-700 leading-relaxed mb-6">祭りやスポーツイベント、コンサートなどの大規模イベントにおける駐車場誘導と交通整理を行います。来場者の安全確保と円滑な車両誘導により、イベントの成功をサポート。倉敷市内の各種イベントで豊富な実績があります。</p>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-blue-900 mr-3 mt-1"></i>
                    <span className="text-gray-700"><strong>駐車場誘導：</strong>効率的な駐車スペース活用と円滑な誘導</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-blue-900 mr-3 mt-1"></i>
                    <span className="text-gray-700"><strong>来場者の安全確保：</strong>歩行者と車両の動線分離による安全管理</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-blue-900 mr-3 mt-1"></i>
                    <span className="text-gray-700"><strong>混雑時の対応：</strong>ピーク時の交通渋滞を最小限に抑制</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-blue-900 mr-3 mt-1"></i>
                    <span className="text-gray-700"><strong>臨時駐車場の運営：</strong>大規模イベント時の臨時駐車場管理</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="mb-20">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <div className="inline-block bg-blue-100 text-blue-900 px-4 py-2 rounded-full text-sm font-semibold mb-4">定期警備</div>
                <h2 className="text-4xl font-bold text-gray-900 mb-6">商業施設の駐車場警備</h2>
                <p className="text-gray-700 leading-relaxed mb-6">ショッピングセンターや商業施設の駐車場における交通誘導と安全管理を実施。混雑時の円滑な車両誘導、歩行者の安全確保、トラブル対応など、お客様が安心して利用できる環境づくりをサポートします。</p>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-blue-900 mr-3 mt-1"></i>
                    <span className="text-gray-700"><strong>平日・休日の定期警備：</strong>営業時間に合わせた柔軟な配置</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-blue-900 mr-3 mt-1"></i>
                    <span className="text-gray-700"><strong>繁忙期の増員対応：</strong>セール期間や年末年始の特別体制</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-blue-900 mr-3 mt-1"></i>
                    <span className="text-gray-700"><strong>トラブル対応：</strong>駐車場内のトラブルに迅速に対処</span>
                  </li>
                  <li className="flex items-start">
                    <i className="ri-checkbox-circle-fill text-blue-900 mr-3 mt-1"></i>
                    <span className="text-gray-700"><strong>お客様対応：</strong>丁寧な接客で施設のイメージ向上に貢献</span>
                  </li>
                </ul>
              </div>
              <div className="order-1 lg:order-2">
                <img src="https://readdy.ai/api/search-image?query=Japanese%20security%20guard%20in%20professional%20uniform%20at%20modern%20shopping%20mall%20parking%20lot%20entrance%2C%20organized%20parking%20area%20with%20clear%20lane%20markings%2C%20shoppers%20and%20vehicles%20visible%2C%20daytime%20bright%20lighting%2C%20professional%20commercial%20photography%2C%20realistic%20high%20quality&width=800&height=600&seq=service104&orientation=landscape" alt="商業施設の駐車場警備" className="w-full h-96 object-cover object-top rounded-xl shadow-lg" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">その他のサービス</h2>
            <p className="text-xl text-gray-600">倉敷市内の様々な現場に対応しています</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-md">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-hospital-line text-3xl text-blue-900"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">医療施設の駐車場管理</h3>
              <p className="text-gray-700 leading-relaxed">病院やクリニックの駐車場における交通誘導と患者様の安全確保を行います。救急車両の優先通行や高齢者への配慮など、医療施設特有のニーズに対応します。</p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-md">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-school-line text-3xl text-blue-900"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">学校行事の交通整理</h3>
              <p className="text-gray-700 leading-relaxed">運動会や文化祭などの学校行事における駐車場誘導と周辺道路の交通整理を実施。児童・生徒の安全を最優先に、保護者の方々の円滑な送迎をサポートします。</p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-md">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-community-line text-3xl text-blue-900"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">マンション工事の警備</h3>
              <p className="text-gray-700 leading-relaxed">マンションの大規模修繕工事や新築工事における交通誘導を行います。住民の方々の日常生活への影響を最小限に抑えながら、安全な工事環境を確保します。</p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-md">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-store-line text-3xl text-blue-900"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">店舗オープン時の警備</h3>
              <p className="text-gray-700 leading-relaxed">新規店舗のグランドオープンやセール開催時の駐車場誘導と交通整理を実施。混雑が予想される日の安全管理と円滑な運営をサポートします。</p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-md">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-roadster-line text-3xl text-blue-900"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">駐車場の定期巡回</h3>
              <p className="text-gray-700 leading-relaxed">月極駐車場やコインパーキングの定期巡回と管理を行います。不正駐車の防止、トラブル対応、清掃確認など、駐車場の適切な運営をサポートします。</p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-md">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-tools-line text-3xl text-blue-900"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">設備工事の交通誘導</h3>
              <p className="text-gray-700 leading-relaxed">電気・ガス・水道などのインフラ工事における交通誘導を実施。ライフラインの工事を安全かつ円滑に進めるための交通管理を行います。</p>
            </div>
          </div>
        </div>
      </section>

      {/* Service Flow */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">ご依頼の流れ</h2>
            <p className="text-xl text-gray-600">お問い合わせから警備実施までの流れ</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-blue-900 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">1</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">お問い合わせ</h3>
              <p className="text-gray-600 text-sm leading-relaxed">お電話またはお問い合わせフォームからご連絡ください。倉敷市内であれば即日対応も可能です。</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-blue-900 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">2</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">現場確認</h3>
              <p className="text-gray-600 text-sm leading-relaxed">現場の状況を確認し、最適な警備計画を立案します。必要に応じて現地調査を実施します。</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-blue-900 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">3</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">お見積り</h3>
              <p className="text-gray-600 text-sm leading-relaxed">警備内容に基づいた詳細なお見積りを提出します。ご予算に応じた柔軟な対応も可能です。</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-blue-900 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">4</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">契約・準備</h3>
              <p className="text-gray-600 text-sm leading-relaxed">契約締結後、警備員の配置や必要な機材の準備を行います。事前打ち合わせも実施します。</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 bg-blue-900 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">5</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">警備実施</h3>
              <p className="text-gray-600 text-sm leading-relaxed">経験豊富なスタッフが現場で交通誘導警備を実施。安全第一で業務を遂行します。</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">交通誘導警備のご依頼・ご相談</h2>
          <p className="text-xl text-blue-100 mb-8">倉敷市を中心に、確かな技術と丁寧な対応で安全を守ります</p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href="tel:086-012-3456" className="bg-white text-blue-900 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 whitespace-nowrap cursor-pointer inline-flex items-center justify-center">
              <i className="ri-phone-fill mr-2"></i>086-012-3456
            </a>
            <Link to="/#contact" className="bg-blue-700 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-600 whitespace-nowrap cursor-pointer inline-flex items-center justify-center">
              <i className="ri-mail-fill mr-2"></i>お問い合わせフォーム
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-xl font-bold mb-4">おかやま警備株式会社</h3>
              <p className="text-gray-400 text-sm leading-relaxed">倉敷市を中心に、確かな技術と丁寧な対応で地域の安全を守ります。</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">サービス</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/services" className="text-gray-400 hover:text-white cursor-pointer">道路工事現場の交通誘導</Link></li>
                <li><Link to="/services" className="text-gray-400 hover:text-white cursor-pointer">建設現場の警備</Link></li>
                <li><Link to="/services" className="text-gray-400 hover:text-white cursor-pointer">イベント会場の交通整理</Link></li>
                <li><Link to="/services" className="text-gray-400 hover:text-white cursor-pointer">商業施設の駐車場警備</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">会社情報</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/company" className="text-gray-400 hover:text-white cursor-pointer">会社概要</Link></li>
                <li><Link to="/recruit" className="text-gray-400 hover:text-white cursor-pointer">求人情報</Link></li>
                <li><Link to="/blog" className="text-gray-400 hover:text-white cursor-pointer">ブログ・新着情報</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">お問い合わせ</h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start">
                  <i className="ri-phone-fill mr-2 mt-1"></i>
                  <a href="tel:086-012-3456" className="text-gray-400 hover:text-white cursor-pointer">086-012-3456</a>
                </li>
                <li className="flex items-start">
                  <i className="ri-printer-fill mr-2 mt-1"></i>
                  <span className="text-gray-400">086-012-3457</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-map-pin-fill mr-2 mt-1"></i>
                  <span className="text-gray-400">岡山市北区○○町1-2-3</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm">&copy; 2025 おかやま警備株式会社. All rights reserved.</p>
            <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white text-sm mt-4 md:mt-0 cursor-pointer">Powered by Readdy</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
